import React, { useEffect, useState } from 'react';
import { FormDataConsumer, useGetOne, useDataProvider } from 'react-admin';
import { AutocompleteInput } from 'react-admin';
import {
    required,
    useNotify,
    Create,
    DateInput,
    FormTab,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TabbedForm,
    TextInput,
} from 'react-admin';
import BoxitPhoneInput from '../components/BoxitPhoneInput';
import useLocalStorage from '../hooks/useLocalStorage';
import { CreateProps, User } from '../types';
import { handleUserError } from './error';
import AndOrInput from '../components/AndOrInput';
import { ReferenceArrayInput } from 'react-admin';
import { AutocompleteArrayInput } from 'react-admin';
import feathersClient from '../rest/feathersClient';
import { Button, Checkbox, Input } from '@material-ui/core';
import { CheckBox } from '@material-ui/icons';
import { FunctionField } from 'react-admin';
import { regex } from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import { CountryCode } from '../constants';
import useIsAdmin from '../hooks/useIsAdmin';
import DocumentUpload from './components/DocumentUpload';

const optionText = user => `${user.name} (${user.id})`;

const isRequired = required();

const idTypeChoices = [
    { id: 'Passport', name: 'Passport' },
    { id: 'EmiratesID', name: 'Emirates ID' },
    { id: 'CivilID', name: 'Civil ID' },
];

const userGenderChoices = [
    { id: 'male', name: 'Male' },
    { id: 'female', name: 'Female' },
];

const languageChoices = [
    { id: 'EN', name: 'English' },
    { id: 'AR', name: 'Arabic' },
];

const statusChoices = [
    { id: 'EXISTING_CUSTOMER', name: 'Existing Customer' },
    { id: 'EX_CUSTOMER', name: 'Ex Customer' },
];

const PartnerCountryPrefill = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const isAdmin = useIsAdmin();

    useEffect(() => {
        if (!values.partnerId) return;

        // Exception: Boxit (id=1) allows choosing country manually
        if (values.partnerId === 1) return;

        dataProvider
            .getOne('partners', { id: values.partnerId })
            .then(({ data }) => {
                const partnerCountryId = data?.countryId || data?.country?.id;
                const partnerCountryCode =
                    data?.countryCode || data?.country?.code;

                if (partnerCountryId) {
                    form.change('countryId', partnerCountryId);
                }

                if (partnerCountryCode) {
                    form.change('countryCode', partnerCountryCode);
                } else if (partnerCountryId && CountryCode[partnerCountryId]) {
                    form.change('countryCode', CountryCode[partnerCountryId]);
                }
            })
            .catch(err => console.error('Error fetching partner:', err));
    }, [values.partnerId]);

    return null;
};

// For Boxit (id=1): derive countryCode from the manually selected country, and keep it locked
const BoxitCountryCodePrefill = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const isAdmin = useIsAdmin();

    useEffect(() => {
        const isBoxitPartner = values.partnerId === 1;
        if (!isBoxitPartner) return;

        if (!values.countryId) {
            form.change('countryCode', null);
            return;
        }

        dataProvider
            .getOne('country', { id: values.countryId })
            .then(({ data: country }) => {
                if (country?.code) {
                    form.change('countryCode', country.code);
                } else if (CountryCode[values.countryId]) {
                    form.change('countryCode', CountryCode[values.countryId]);
                }
            })
            .catch(err => console.error('Error fetching country:', err));
    }, [values.partnerId, values.countryId]);

    return null;
};

// Define the email validation pattern
const emailValidation = regex(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    'Please enter a valid email address'
);

// Define name validation to prevent special characters
const nameValidation = regex(
    /^[\u0600-\u06FFa-zA-Z0-9\s]*$/,
    "Name cannot contain special characters like @ or '"
);
const UserCreate: React.FC<CreateProps<User>> = ({
    permissions = [],
    ...props
}) => {
    const isAdmin = useIsAdmin(permissions);
    const notify = useNotify();
    const [currentUserId] = useLocalStorage('userId', null);
    const { data: currentUser } = useGetOne('users', currentUserId);
    const [isCreateNewModule, setIsCreateNewModule] = useState(false);
    const [isGeneric, setIsGeneric] = useState(false);
    const [tagName, setTagName] = useState('');
    const [isNewTag, setIsNewTag] = useState(false);

    const addTag = (id, tags) => {
        console.log(notify);
        const tagService = feathersClient.service('tags');
        const userService = feathersClient.service('users');
        tagService
            .create({
                name: tagName,
                module: isGeneric ? 'Generic' : 'User',
            })
            .then(res => {
                window.location.reload();
            })
            .catch(err => {
                notify(err.message, 'error');
            });
    };

    return (
        <Create
            successMessage="resources.users.notification.create_success"
            onFailure={error => handleUserError(error, notify)}
            {...props}
        >
            <TabbedForm variant="outlined" redirect="show">
                <FormTab label="User">
                    <PartnerCountryPrefill />
                    <BoxitCountryCodePrefill />
                    {(permissions.indexOf('staff') !== -1 || isAdmin) && (
                        <ReferenceInput
                            source="partnerId"
                            reference="partners"
                            resource="partners"
                            label="Partner"
                            validate={isRequired}
                            defaultValue={1}
                        >
                            <SelectInput optionText="name" />
                        </ReferenceInput>
                    )}
                    <TextInput
                        source="name"
                        validate={[isRequired, nameValidation]}
                    />
                    <TextInput
                        source="email"
                        type="email"
                        validate={[isRequired, emailValidation]}
                    />
                    <FormDataConsumer>
                        {({ formData }) => (
                            <BoxitPhoneInput
                                source="phone"
                                validate={isRequired}
                                country={formData.countryId}
                                defaultCountry={currentUser?.countryId}
                            />
                        )}
                    </FormDataConsumer>
                    <FormDataConsumer>
                        {({ formData }) => (
                            <BoxitPhoneInput
                                source="phone2"
                                country={formData.countryId}
                                defaultCountry={currentUser?.countryId}
                            />
                        )}
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData }) => (
                            <ReferenceInput
                                source="countryId"
                                reference="country"
                                resource="country"
                                label="Country"
                                validate={isRequired}
                                defaultValue={currentUser?.countryId}
                            >
                                <SelectInput
                                    optionText="name"
                                    disabled={
                                        !isAdmin &&
                                        !!formData.partnerId &&
                                        formData.partnerId !== 1
                                    }
                                />
                            </ReferenceInput>
                        )}
                    </FormDataConsumer>

                    <FormDataConsumer>
                        {({ formData }) => (
                            <TextInput
                                source="countryCode"
                                label="Country Code"
                                disabled={!isAdmin && !!formData.partnerId}
                            />
                        )}
                    </FormDataConsumer>

                    <ReferenceInput
                        label="Assignee"
                        source="assigneeId"
                        reference="users"
                        resource="users"
                    >
                        <AutocompleteInput
                            shouldRenderSuggestions={val =>
                                val.trim().length > 1
                            }
                            optionText={optionText}
                        />
                    </ReferenceInput>
                    <div>
                        <Checkbox
                            checked={isNewTag}
                            onChange={() => {
                                setIsNewTag(prev => !prev);
                            }}
                        />
                        <span>New Tag?</span>
                    </div>

                    {!isNewTag ? (
                        <ReferenceArrayInput
                            source="tags"
                            reference="tags"
                            label="tags"
                        >
                            <AutocompleteArrayInput />
                        </ReferenceArrayInput>
                    ) : (
                        <FunctionField
                            label="Add new Tag"
                            render={record => (
                                <div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '.5rem',
                                        }}
                                    >
                                        <Input
                                            onChange={e =>
                                                setTagName(e.target.value)
                                            }
                                            placeholder="Tag name"
                                        />
                                        <Button
                                            style={{
                                                backgroundColor: '#09C2F3',
                                                width: '8rem',
                                                height: '3rem',
                                                color: 'white',
                                            }}
                                            disabled={tagName === ''}
                                            onClick={() => {
                                                addTag(record.id, record.tags);
                                            }}
                                        >
                                            Add new tag
                                        </Button>
                                    </div>
                                    <div>
                                        <Checkbox
                                            placeholder="Is Generic"
                                            checked={isGeneric}
                                            onChange={() => {
                                                setIsGeneric(prev => !prev);
                                            }}
                                        />
                                        <span>Is generic?</span>
                                    </div>
                                </div>
                            )}
                        />
                    )}
                </FormTab>

                <FormTab label="Personal Data">
                    <TextInput
                        source="englishName"
                        label="English Name"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="arabicName"
                        label="Arabic Name"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="firstNameEnglish"
                        label="First Name (English)"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="firstNameArabic"
                        label="First Name (Arabic)"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="lastNameEnglish"
                        label="Last Name (English)"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="lastNameArabic"
                        label="Last Name (Arabic)"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="nicknameEnglish"
                        label="Nickname (English)"
                        validate={nameValidation}
                    />
                    <TextInput
                        source="nicknameArabic"
                        label="Nickname (Arabic)"
                        validate={nameValidation}
                    />
                    <SelectInput
                        source="gender"
                        label="Gender"
                        choices={userGenderChoices}
                    />
                    <SelectInput
                        source="currentStatus"
                        label="Current Status"
                        choices={statusChoices}
                        disabled
                    />
                    <SelectInput
                        source="language"
                        label="Language"
                        choices={languageChoices}
                        defaultValue="EN"
                    />
                </FormTab>

                <FormTab label="Documents">
                    <DocumentUpload onCreate={true} />
                </FormTab>
            </TabbedForm>
        </Create>
    );
};

export default UserCreate;
